import {
  API_ERROR_CODES,
  parseApiError,
} from "@/lib/api/api-error";
import { SIGNUP_FIELD_LIMITS } from "@/lib/api/signup-form";

export interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

export function isResetPasswordFormValid(form: ResetPasswordFormValues): boolean {
  return (
    form.password.length >= SIGNUP_FIELD_LIMITS.passwordMin &&
    form.password.length <= SIGNUP_FIELD_LIMITS.password &&
    form.password === form.confirmPassword
  );
}

export function toResetPasswordRequest(token: string, form: ResetPasswordFormValues) {
  return {
    token,
    password: form.password,
  };
}

const RESET_PASSWORD_MESSAGES = {
  network:
    "We couldn't reach the server. Check your connection and try again.",
  invalidToken:
    "This reset link is invalid or has expired. Request a new one.",
  malformedToken: "This reset link is malformed. Request a new one.",
  validation:
    "Please check your password and try again. It must be at least 8 characters and both fields must match.",
  generic: "Something went wrong. Please try again later.",
} as const;

export function shouldOfferResetPasswordRequestLink(
  code?: string,
  message?: string,
): boolean {
  if (
    code === API_ERROR_CODES.invalidResetToken ||
    code === API_ERROR_CODES.malformedResetToken ||
    code === API_ERROR_CODES.adminInvalidResetToken ||
    code === API_ERROR_CODES.adminMalformedResetToken
  ) {
    return true;
  }

  const normalized = message?.toLowerCase() ?? "";
  return (
    normalized.includes("invalid or has expired") ||
    normalized.includes("malformed")
  );
}

export function getResetPasswordErrorMessage(err: unknown): string {
  const { status, code, message, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return RESET_PASSWORD_MESSAGES.network;
  }

  if (
    status === 400 &&
    (code === API_ERROR_CODES.invalidResetToken ||
      code === API_ERROR_CODES.adminInvalidResetToken)
  ) {
    return message ?? RESET_PASSWORD_MESSAGES.invalidToken;
  }

  if (
    status === 400 &&
    (code === API_ERROR_CODES.malformedResetToken ||
      code === API_ERROR_CODES.adminMalformedResetToken)
  ) {
    return message ?? RESET_PASSWORD_MESSAGES.malformedToken;
  }

  if (status === 400) {
    return message ?? RESET_PASSWORD_MESSAGES.validation;
  }

  return RESET_PASSWORD_MESSAGES.generic;
}

export const RESET_PASSWORD_SUCCESS_MESSAGE =
  "Your password has been updated. You can log in now.";
